/**
 * Exportacao de listagens em Excel (.xlsx).
 *
 * Substitui os CSVs gerados no navegador: o Excel abria os CSV com acentos e
 * simbolos quebrados e tudo como texto. Aqui o arquivo ja nasce como planilha,
 * com numeros preservados e largura de coluna ajustada.
 *
 * Aceita:
 *   - array de objetos  -> as chaves viram o cabecalho
 *   - array de arrays   -> a primeira linha e o cabecalho
 */

const MAX_COLUMN_WIDTH = 60;
const SAMPLE_ROWS_FOR_WIDTH = 300;

function toAoa(rows) {
  if (!Array.isArray(rows) || !rows.length) return [];
  if (Array.isArray(rows[0])) return rows;

  const header = Object.keys(rows[0]);
  return [header, ...rows.map((row) => header.map((key) => normalizeCell(row[key])))];
}

function normalizeCell(value) {
  if (value === undefined || value === null) return "";
  if (Array.isArray(value)) return value.join(" | ");
  if (value instanceof Date) return value;
  return value;
}

function columnWidths(aoa) {
  const header = aoa[0] || [];
  return header.map((_, index) => {
    const lengths = aoa.slice(0, SAMPLE_ROWS_FOR_WIDTH).map((row) => String(row[index] === undefined || row[index] === null ? "" : row[index]).length);
    return { wch: Math.min(MAX_COLUMN_WIDTH, Math.max(8, ...lengths) + 2) };
  });
}

function ensureExtension(fileName) {
  const base = String(fileName || "export").replace(/\.csv$/i, "").replace(/\.xlsx$/i, "");
  return `${base}.xlsx`;
}

/**
 * Gera e baixa o .xlsx. Retorna false quando nao ha linhas.
 */
export async function downloadXlsx(rows, fileName, options = {}) {
  const aoa = toAoa(rows);
  if (!aoa.length) return false;

  const XLSX = await import("xlsx");
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  worksheet["!cols"] = columnWidths(aoa);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, String(options.sheetName || "Dados").slice(0, 31));
  XLSX.writeFile(workbook, ensureExtension(fileName));

  return true;
}

/**
 * Gera e baixa um .xlsx com varias abas: [{ name, rows }]. Abas sem linhas sao ignoradas.
 * Retorna false quando nenhuma aba tem dados.
 */
export async function downloadXlsxSheets(sheets, fileName) {
  const prepared = (Array.isArray(sheets) ? sheets : [])
    .map((sheet) => ({ name: String(sheet.name || "Dados").slice(0, 31), aoa: toAoa(sheet.rows) }))
    .filter((sheet) => sheet.aoa.length);
  if (!prepared.length) return false;

  const XLSX = await import("xlsx");
  const workbook = XLSX.utils.book_new();
  prepared.forEach((sheet) => {
    const worksheet = XLSX.utils.aoa_to_sheet(sheet.aoa);
    worksheet["!cols"] = columnWidths(sheet.aoa);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name);
  });
  XLSX.writeFile(workbook, ensureExtension(fileName));

  return true;
}

/**
 * Baixa um arquivo binario devolvido pela API (responseType: 'blob').
 */
export function downloadBlob(data, fileName, mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
  const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = ensureExtension(fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export default { downloadXlsx, downloadXlsxSheets, downloadBlob };
